import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import CSSTransition from 'react-transition-group/CSSTransition';
import cond from 'lodash/cond';
import styled from 'styled-components';

import { SuiContainer } from '@splashthat/sui';
import { TRANSITION_TIMEOUT } from 'common/constants';

import { ENTITIES_KEY_NODE } from 'common/entities/formNode';
import { ENTITIES_KEY_FORM } from 'common/entities/form';
import { ENTITIES_KEY_TICKET_TYPES } from 'common/entities/ticketType';
import { isFormTypeTicketSale, isFormTypeTicketSelection } from 'common/entities/form/selectors';
import PermissionCheckWrapper from 'utils/PermissionCheckWrapper';
import { getIsMultisessionEvent } from 'common/entities/event/selectors';
import { useFlags } from 'utils/react-context';

import { FormBuilderContext } from './FormBuilder';
import EditFormNodePanel from '../EditView/EditFormNodePanel';
import EditFormHeadlinePanel from '../EditView/EditFormHeadlinePanel';
import EditFormSubmitButtonPanel from '../EditView/EditFormSubmitButtonPanel';
import FormEditorLayoutHeader from '../LayoutView/FormEditorLayoutHeader';
import FormEditorLayout from '../LayoutView/FormEditorLayout';
import FormErrorMessageModal from '../FormErrorMessage/FormErrorMessageModal';
import FormLogicModal from '../FormLogic/FormLogicModal';
import FormStyleEditor, { FormStylingDisabledWrapper } from '../FormStyleEditor';
import FormStyleTicketEditor from '../FormStyleEditor/FormStyleTicketEditor/FormStyleTicketEditor';
import { FormEditorLayoutSessions } from '../LayoutView/FormEditorLayoutSessions';
import ButtonNode from '../LayoutView/ButtonNode';
import { EditFormSessionsPanel } from '../EditView/EditFormSessionsPanel';

import {
    getIsEntityBeingEdited,
    getEditEntityType,
    getFormId,
    getActiveSidebarTab,
    getActiveFormEditView,
} from '../../selectors';

import {
    FORM_EDIT_VIEW_HEADLINE,
    FORM_EDIT_VIEW_SUBMIT_BUTTON,
    FORM_EDIT_VIEW_TICKET_STYLES,
    FORM_EDIT_VIEW_SESSION_REGISTRATION,
} from '../../constants/formEditViews';
import { QUESTIONS, STYLE } from '../../constants/sidebarTabValues';

export const RIGHT = 'right';
export const LEFT = 'left';

export const EditViewCSSTransition = ({ direction, ...props }) => (
    <CSSTransition
        appear
        mountOnEnter
        unmountOnExit
        timeout={TRANSITION_TIMEOUT}
        classNames={`sidebar-transition-fading-${direction}`}
        {...props}
    />
);

EditViewCSSTransition.propTypes = {
    direction: PropTypes.oneOf([RIGHT, LEFT]).isRequired,
};

const RelativeContainer = styled.div`
    position: relative;
    display: flex;
`;

const OverflowContainer = styled.div`
    overflow-y: auto;
    width: 100%;
`;

const SuiContainerWithOpacity = styled(SuiContainer)`
    ${({ shoulddisable }) =>
        shoulddisable === 'disabled' &&
        `
        && {
            opacity: 0.4;
            cursor: not-allowed;
        }
    `}
`;

const FormEditor = (props) => {
    const flags = useFlags();

    return (
        <FormBuilderContext.Consumer>
            {({ permissions }) => (
                <PermissionCheckWrapper permission={permissions.canUserEditForm}>
                    {() => (
                        <SuiContainerWithOpacity
                            variation="flex-vertical"
                            className="FormEditor"
                            shoulddisable={!permissions.canUserEditForm ? 'disabled' : 'active'}
                        >
                            <EditViewCSSTransition in={!props.isEntityBeingEdited} direction={LEFT}>
                                <SuiContainer variation="flex-vertical">
                                    {!props.isTypeTicketSale && (
                                        <FormEditorLayoutHeader
                                            stylingEnabled={props.stylingEnabled}
                                            canEditForm={permissions.canUserEditForm}
                                        />
                                    )}
                                    <RelativeContainer>
                                        <EditViewCSSTransition
                                            in={props.activeSidebarTab === QUESTIONS}
                                            direction={LEFT}
                                        >
                                            <OverflowContainer>
                                                {cond([
                                                    [() => props.isTypeTicketSale, () => <FormEditorLayout />],
                                                    [
                                                        () =>
                                                            props.isMultisessionEvent &&
                                                            !props.isTypeTicketSelection &&
                                                            flags.formRegistrationForm,
                                                        () => <FormEditorLayoutSessions />,
                                                    ],
                                                    [
                                                        () => true,
                                                        () => (
                                                            <FormEditorLayout>
                                                                <ButtonNode />
                                                            </FormEditorLayout>
                                                        ),
                                                    ],
                                                ])()}
                                            </OverflowContainer>
                                        </EditViewCSSTransition>
                                        {props.stylingEnabled && (
                                            <EditViewCSSTransition
                                                in={props.activeSidebarTab === STYLE}
                                                direction={RIGHT}
                                            >
                                                <OverflowContainer>
                                                    <FormStylingDisabledWrapper>
                                                        <FormStyleEditor />
                                                    </FormStylingDisabledWrapper>
                                                </OverflowContainer>
                                            </EditViewCSSTransition>
                                        )}
                                    </RelativeContainer>
                                </SuiContainer>
                            </EditViewCSSTransition>

                            <EditViewCSSTransition
                                in={props.entityTypeBeingEdited === ENTITIES_KEY_NODE}
                                direction={RIGHT}
                            >
                                <EditFormNodePanel />
                            </EditViewCSSTransition>

                            <EditViewCSSTransition
                                in={
                                    props.entityTypeBeingEdited === ENTITIES_KEY_FORM &&
                                    props.activeFormEditView === FORM_EDIT_VIEW_HEADLINE
                                }
                                direction={RIGHT}
                            >
                                <EditFormHeadlinePanel />
                            </EditViewCSSTransition>

                            <EditViewCSSTransition
                                in={
                                    props.entityTypeBeingEdited === ENTITIES_KEY_FORM &&
                                    props.activeFormEditView === FORM_EDIT_VIEW_SUBMIT_BUTTON
                                }
                                direction={RIGHT}
                            >
                                <EditFormSubmitButtonPanel />
                            </EditViewCSSTransition>

                            <EditViewCSSTransition
                                in={
                                    props.entityTypeBeingEdited === ENTITIES_KEY_TICKET_TYPES &&
                                    props.activeFormEditView === FORM_EDIT_VIEW_TICKET_STYLES
                                }
                                direction={RIGHT}
                            >
                                <FormStyleTicketEditor />
                            </EditViewCSSTransition>

                            <EditViewCSSTransition
                                in={
                                    props.entityTypeBeingEdited === ENTITIES_KEY_FORM &&
                                    props.activeFormEditView?.type === FORM_EDIT_VIEW_SESSION_REGISTRATION
                                }
                                direction={RIGHT}
                            >
                                <EditFormSessionsPanel />
                            </EditViewCSSTransition>

                            <FormLogicModal />
                            <FormErrorMessageModal />
                        </SuiContainerWithOpacity>
                    )}
                </PermissionCheckWrapper>
            )}
        </FormBuilderContext.Consumer>
    );
};

FormEditor.propTypes = {
    isEntityBeingEdited: PropTypes.bool.isRequired,
    entityTypeBeingEdited: PropTypes.string,
    activeFormEditView: PropTypes.string,
    activeSidebarTab: PropTypes.string.isRequired,
    stylingEnabled: PropTypes.bool.isRequired,
    isTypeTicketSale: PropTypes.bool.isRequired,
    isTypeTicketSelection: PropTypes.bool.isRequired,
    isMultisessionEvent: PropTypes.bool.isRequired,
};

FormEditor.defaultProps = {
    entityTypeBeingEdited: '',
    activeFormEditView: null,
};

const mapStateToProps = (state, props) => {
    const formId = getFormId(state);
    const isEntityBeingEdited = getIsEntityBeingEdited(state);
    const entityTypeBeingEdited = isEntityBeingEdited ? getEditEntityType(state) : undefined;
    const activeFormEditView = getActiveFormEditView(state);
    const isTypeTicketSale = isFormTypeTicketSale(state, formId);
    const isTypeTicketSelection = isFormTypeTicketSelection(state, formId);
    const isMultisessionEvent = getIsMultisessionEvent(state);

    return {
        isEntityBeingEdited,
        entityTypeBeingEdited,
        activeFormEditView,
        activeSidebarTab: formId ? getActiveSidebarTab(state) : null,
        stylingEnabled: props.stylingEnabled,
        isTypeTicketSale,
        isTypeTicketSelection,
        isMultisessionEvent,
    };
};

const ConnectedComponent = connect(mapStateToProps, {})(FormEditor);

ConnectedComponent.propTypes = {
    stylingEnabled: PropTypes.bool.isRequired,
};

export default ConnectedComponent;
